import {
  HttpErrorResponse,
  type HttpInterceptorFn,
} from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { tap } from 'rxjs';
import { Endpoint } from 'utils/enums/endpoint.util';
import { AccessStore } from '../stores/access.store';

/** Refetch at most once per this window — a burst of 403s is one stale profile. */
export const ACCESS_REFRESH_THROTTLE_MS = 10_000;

const PROFILE_PATH = `api/${Endpoint.ACCESS}/me`;

let lastRefreshAt = 0;

/** Test seam: forget the throttle so each spec starts cold. */
export function resetAccessRefreshThrottle(): void {
  lastRefreshAt = 0;
}

/**
 * The access profile (GET /access/me) is fetched once per session and every
 * nav item and route guard is computed from it. When an admin changes the
 * caller's role or seats while their tab is open, the UI keeps offering the
 * old surfaces and the API refuses them with 403 — the "link shows but
 * clicking it fails" shape. A 403 from the API is therefore the signal that
 * the cached profile may be stale: reload it (throttled), and the computed
 * nav/guards converge without a page refresh. The error itself still
 * propagates to the caller.
 *
 * The store is resolved lazily, inside the error branch, never at request
 * time: AccessStore's computed layer injects UserStore, whose constructor
 * issues HTTP — so an eager inject(AccessStore) here re-enters the store
 * while it is being built (NG0200, app never boots). Errors arrive
 * asynchronously, after construction has finished, which is what makes the
 * lazy lookup safe.
 */
export const accessRefreshInterceptorFn: HttpInterceptorFn = (req, next) => {
  const injector = inject(Injector);
  return next(req).pipe(
    tap({
      error: (error: unknown) => {
        if (
          !(error instanceof HttpErrorResponse) ||
          error.status !== 403 ||
          !req.url.includes('api/') ||
          req.url.includes(PROFILE_PATH)
        ) {
          return;
        }
        const now = Date.now();
        if (now - lastRefreshAt < ACCESS_REFRESH_THROTTLE_MS) {
          return;
        }
        lastRefreshAt = now;
        injector
          .get(AccessStore)
          .loadMyAccess()
          .subscribe({
            // A failed refresh is not this request's error; the store already
            // logged it and keeps the last known profile.
            error: () => undefined,
          });
      },
    })
  );
};
